import { useState, useEffect } from "react";
import { Typography, Box, IconButton, BookmarkIcon, BookmarkBorderIcon, ShareButton, ArrowBackIcon, 
  SVGIcon, Button, MuiAlert
} from "src/components";
import { useNavigate } from "react-router-dom";
import { api, useAuth } from "src/utils";
import { useIsMobile, useSnackbar } from "src/hooks";
import { DesktopProfileModal } from "src/pages/profile";
import HandbookBreadcrumbs from "./handbook-breadcrumbs";
import { getCategoryBySlug } from "src/data";

import alaskaImg from "src/assets/handbook-header.png";

export default function HandbookHeader({
  id,
  title,
  description,
  showControlButtons = false,
  category,
  subcategory,
  coverImageUrl,
  displayBackButton = false,
  displayErrorAlert= false
}) {
  const [displayLogin, setDisplayLogin] = useState(false);
  const [bookmarkIds, setBookmarkIds] = useState([]);
  const [loadingBookmarkRequest, setLoadingBookmarkRequest] = useState(false);
  
  const categoryData = getCategoryBySlug(category || 'routes');
  const iconComponent = categoryData?.icon;
  const imageUrl = coverImageUrl ? coverImageUrl : alaskaImg;

  const navigate = useNavigate();
  const { user, setUser } = useAuth();
  const { openSnackbar} = useSnackbar();
  const isMobile = useIsMobile();

  useEffect(() => {
    if (user === null) {
      setBookmarkIds([]);
      return;
    }
    setBookmarkIds(user.bookmarks || []);
  }, [user]);

  const handleBookmarkClick = async () => {
    if (!user) {
      isMobile ? navigate("/profile") : setDisplayLogin(true);
      return;
    }
    try {
      setLoadingBookmarkRequest(true);
      const res = await api.post(`/user/bookmarks/${id}`);
      const updatedBookmarks = res.data;
      setBookmarkIds(updatedBookmarks);
      setUser((prev) => ({ ...prev, bookmarks: updatedBookmarks }));
    } catch (err) {
      console.error("Error toggling bookmark", err);
      openSnackbar("Failed to toggle bookmark", "error");
    } finally {
      setLoadingBookmarkRequest(false);
    }
  };

  return (
    <>
      <Box
        sx={{
          position: "relative",
          height: isMobile ? "100vh" : "calc(100vh + 60px)",
          width: "100%",
          backgroundImage: `url(${imageUrl})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          color: "#fff",
          zIndex: 1,
        }}
      >

        {/* Overlay */}
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            backgroundColor: "rgba(0,0,0,0.5)",
            zIndex: 0
          }}
        />

        {displayErrorAlert && (
          <MuiAlert 
            severity="error" 
            sx={{ 
              position: 'absolute',
              top: isMobile ? 80 : 150,
              left: isMobile ? 10 : 40,
              zIndex: 10,
              borderRadius: 2,
              '& .MuiAlert-icon': { fontSize: '1.5rem' }
            }}
          >
            <Typography variant="h6">Oops! Something went wrong</Typography>
            <Typography variant="body2">Failed to load article content</Typography>
          </MuiAlert>
        )}

        {displayBackButton && (
          <Box sx={{ position: "absolute", top: isMobile ? 18 : 80, left: isMobile ? 10 : 40, zIndex: 10 }}>
              <Button
                variant="text"
                disableRipple
                color="#fff"
                startIcon={<ArrowBackIcon />}
                sx={{ borderRadius: 2, mt: 2 }}
                onClick={() => navigate("/handbook")}>
                Back To Handbook
              </Button>
          </Box>
        )}

        {/* Control Buttons */}
        {showControlButtons && id && (<>
          {/* Desktop Breadcrumbs - only show on desktop */}
          {!isMobile && (
            <Box sx={{ position: "absolute", top: 80, left: 40, zIndex: 10 }}>
              <HandbookBreadcrumbs
                category={category}
                subcategory={subcategory}
                textColor="#fff"
              />
            </Box>
          )}

          <Box sx={{ 
            position: "absolute", 
            top: isMobile ? "auto" : 70, 
            bottom: isMobile ? 50 : "auto", // 100px from bottom to clear the wave
            right: isMobile ? 20 : 40, 
            zIndex: 10, 
            display: "flex", 
            gap: isMobile ? 0 : 1
          }}>
            <IconButton
              size="small"
              sx={{ 
                color: "#fff",
                // Larger touch targets for mobile
                ...(isMobile && {
                  width: 44,
                  height: 44,
                })
              }}
              onClick={handleBookmarkClick}
              loading={loadingBookmarkRequest}
            >
              {bookmarkIds.includes(id) ? (
                <BookmarkIcon fontSize="small" />
              ) : (
                <BookmarkBorderIcon fontSize="small" />
              )}
            </IconButton>
            <ShareButton 
              size="small" 
              iconColor="#fff" 
              sx={isMobile ? { width: 44, height: 44 } : {}}
            />
          </Box>
        </>)}

        {/* Centered Content */}
        <Box
          sx={{
            zIndex: 2,
            position: "relative",
            height: "100%",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            textAlign: "center",
            px: isMobile ? 2 : 4,
            transform: "translateY(-5%)",
          }}
        >
          
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <SVGIcon 
              icon={iconComponent} 
              size={100} 
              color="#fff" 
            />
          </Box>
          
          <Typography
            variant="h1"
            fontWeight="800"
            sx={{
              fontSize: isMobile 
                ? 'clamp(1.5rem, 8vw, 2.5rem)' 
                : 'clamp(2rem, 6vw, 4rem)', 
              letterSpacing: 1,
              lineHeight: 1.4,
              textShadow: '0 4px 10px rgba(0,0,0,0.6)',
              mb: 2,
            }}
          >
            {title}
          </Typography>

          {description && (
            <Typography
              variant="body1"
              sx={{
                maxWidth: isMobile ? 350 : 600,
                fontSize: isMobile ? '0.9rem' : '1rem',
                textShadow: "0 1px 4px rgba(0,0,0,0.4)",
              }}
            >
              {description}
            </Typography>
          )}

        </Box>

        {/* Wavy Bottom */}
        <Box sx={{ 
          position: "absolute", 
          bottom: -1, 
          left: 0, 
          right: 0, 
          lineHeight: 0,
          zIndex: 1 
        }}>
          <svg viewBox="0 0 1440 150" preserveAspectRatio="none" style={{ width: '100%', height: 80 }}>
            <path
              d="M0,50 C360,120 1080,120 1440,50 L1440,150 L0,150 Z"
              fill="#FAFAFA"
            />
          </svg>
        </Box>

      </Box>

      {/* Mobile Breadcrumbs - positioned below hero image */}
      {isMobile && showControlButtons && (
        <Box sx={{ 
          px: 2, 
          py: 1,
          backgroundColor: "#FAFAFA",
          width: '100%',
          float: 'left'
        }}>
          <HandbookBreadcrumbs
            category={category}
            subcategory={subcategory}
            textColor="#333"
          />
        </Box>
      )}

      <DesktopProfileModal
        displayLogin={displayLogin}
        setDisplayLogin={setDisplayLogin}
      />
    </>
  );
}